import { useRef } from "react";
import { AbortError, createFailure, FailureSeverity, validationFailed, validationSuccessful } from "sprint-common-web";

import { CreateEntityResponse } from "../../../../common/api/common/CreateEntityResponse";
import { ContentSource } from "../../../../common/types/ContentSource";
import { CreateTvSeriesRequest } from "../../api/CreateTvSeriesRequest";
import { TvSeriesReference } from "../../api/GetTvSeriesBySimilarTitleResponse";
import { useAssetsManageTvSeriesApi } from "../../api/useAssetsManageTvSeriesApi";
import { TvSeriesListingItem } from "../TvSeriesListingItem";
import { AddTvSeriesFormInputs } from "./CreateTvSeriesFormInputs";

export const useCreateTvSeriesForm = () => {
    const { createTvSeries, getTvSeriesBySimilarTitle } = useAssetsManageTvSeriesApi();

    const abortController = useRef<AbortController>(new AbortController());

    const createNewTvSeries = async (inputs: AddTvSeriesFormInputs): Promise<TvSeriesListingItem> => {
        return createTvSeries(createRequest(inputs)).then((response: CreateEntityResponse) => {
            return {
                id: response.entityId,
                title: inputs.title,
                orphaned: true,
                includedSources: [ContentSource.Sprint],
                isModified: true,
                orphanedDetails: []
            };
        });
    };

    const getTvSeriesWithSimilarTitle = async (title: string): Promise<TvSeriesReference[]> => {
        if (title) {
            const response = await getTvSeriesBySimilarTitle({ title }, abortController.current.signal);

            return response.items;
        }

        return [];
    };

    const validateTitle = (input: string | undefined) => {
        abortController.current.abort();
        abortController.current = new AbortController();

        if (!input) {
            return validationSuccessful();
        }

        return getTvSeriesWithSimilarTitle(input)
            .then((tvSeriesList: TvSeriesReference[]) => {
                if (!tvSeriesList || tvSeriesList.length < 1) return validationSuccessful();

                const warningMessage = `Similar titles: ${tvSeriesList.map((item: TvSeriesReference) => item.title)}`;
                const failure = createFailure(warningMessage, "tvSeries.SimilarTitle", FailureSeverity.Warning);

                return validationFailed([failure]);
            })
            .catch((error: Error) => {
                if (error instanceof AbortError) {
                    return validationSuccessful();
                }

                throw error;
            });
    };

    return {
        createNewTvSeries,
        validateTitle
    };
};

const createRequest = (inputs: AddTvSeriesFormInputs): CreateTvSeriesRequest => {
    return {
        title: inputs.title
    };
};
